import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/api";
import { PERMISSIONS, canFeatureForRole } from "../../utils/permissions";
import { useRolePermissionMatrix } from "../../hooks/useRolePermissionMatrix";
import HandoverChecklist from "../../components/handover/HandoverChecklist";
import HandoverCommentsBox from "../../components/handover/HandoverCommentsBox";
import IssueDescriptionModal from "../../components/modals/IssueDescriptionModal";
import Loader from "../../components/common/Loader";
import BadgeStatus from "../../components/common/BadgeStatus";
import Pagination from "../../components/common/Pagination";
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from "../../utils/settings";
import "../../styles/Handover.css";
import "../../styles/Table.css";

const MyAssetHandoverPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || "{}");
      return String(u?.role || "").trim().toLowerCase();
    } catch {
      return "";
    }
  }, []);
  const canViewMyAssetHandover = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_MY_ASSET_HANDOVER,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!canViewMyAssetHandover) {
      toast.error("You do not have permission to access My Asset Handover");
      const dash = location.pathname.startsWith("/admin")
        ? "/admin/dashboard"
        : "/employee/dashboard";
      navigate(dash, { replace: true });
    }
  }, [permissionsReady, canViewMyAssetHandover, navigate, location.pathname]);

  const [loading, setLoading] = useState(false);
  const [handovers, setHandovers] = useState([]);
  const [selectedHandovers, setSelectedHandovers] = useState([]);
  const [actionType, setActionType] = useState(null); // 'confirm' or 'report'
  const [comments, setComments] = useState("");
  const [issues, setIssues] = useState("");
  const [received, setReceived] = useState(false);
  const [inspected, setInspected] = useState(false);
  const [responsible, setResponsible] = useState(false);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  
  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);
  const [totalHandovers, setTotalHandovers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pendingFilter, setPendingFilter] = useState("pending_only");

  useEffect(() => {
    if (!permissionsReady || !canViewMyAssetHandover) return;
    fetchHandovers();
  }, [currentPage, itemsPerPage, pendingFilter, permissionsReady, canViewMyAssetHandover]);

  // Refresh data when returning to this page from navigation
  useEffect(() => {
    if (!permissionsReady || !canViewMyAssetHandover) return;
    // Only refresh if we're on the MyAssetHandoverPage route (not detail pages)
    if (location.pathname === "/employee/my-asset-handover" || location.pathname === "/admin/my-asset-handover") {
      const timer = setTimeout(() => {
        fetchHandovers();
      }, 100); // Small delay to ensure navigation is complete

      return () => clearTimeout(timer);
    }
  }, [location.pathname, permissionsReady, canViewMyAssetHandover]);

  const fetchHandovers = async () => {
    if (!permissionsReady || !canViewMyAssetHandover) return;
    try {
      setLoading(true);

      // Get current user for fallback filtering
      const userStr = sessionStorage.getItem("user");
      if (!userStr) {
        setHandovers([]);
        setTotalHandovers(0);
        setTotalPages(1);
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user._id;

      // For "My Asset Handover" page, always show only current user's handovers
      // Pass myHandoversOnly parameter to ensure backend filters correctly for admins too
      const params = {
        acknowledgementStatus: "pending",
        pendingFilter: pendingFilter,
        myHandoversOnly: true, // Ensure only current user's handovers are shown
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await api.getHandovers(params);
      if (response.success) {
        const allHandovers = response.data || [];

        // Fallback: Filter by current user if backend didn't filter correctly
        const myHandovers = allHandovers.filter((h) => {
          if (!h.assignedTo) return false;

          const assignedToId =
            h.assignedTo._id || h.assignedTo.id || h.assignedTo;
          const currentUserId = userId;

          // Compare as strings to handle ObjectId vs string mismatches
          const matches =
            assignedToId?.toString() === currentUserId?.toString();

          return matches;
        });

        // Use backend pagination data if available, otherwise calculate from filtered results
        if (response.total !== undefined && response.totalPages !== undefined) {
          setTotalHandovers(response.total);
          setTotalPages(response.totalPages);
          setHandovers(allHandovers);
        } else {
          // Fallback: use filtered results for pagination
          setTotalHandovers(myHandovers.length);
          setTotalPages(Math.ceil(myHandovers.length / itemsPerPage));
          // Apply client-side pagination
          const startIndex = (currentPage - 1) * itemsPerPage;
          const endIndex = startIndex + itemsPerPage;
          setHandovers(myHandovers.slice(startIndex, endIndex));
        }
      } else {
        setHandovers([]);
        setTotalHandovers(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching handovers:", error);
      toast.error("Error fetching handovers");
      setHandovers([]);
      setTotalHandovers(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHandover = (handoverId) => {
    setSelectedHandovers((prev) => {
      if (prev.includes(handoverId)) {
        return prev.filter((id) => id !== handoverId);
      } else {
        return [...prev, handoverId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedHandovers.length === handovers.length) {
      setSelectedHandovers([]);
    } else {
      setSelectedHandovers(handovers.map((h) => h.id || h._id));
    }
  };

  const handleViewDetails = (handoverId) => {
    // Determine the base path based on current location
    const isAdmin = location.pathname.startsWith('/admin');
    const basePath = isAdmin ? '/admin/my-asset-handover' : '/employee/my-asset-handover';
    navigate(`${basePath}/${handoverId}`);
  };

  const handleConfirmReceived = async () => {
    if (selectedHandovers.length === 0) {
      toast.error("Please select at least one asset");
      return;
    }

    if (!received || !inspected || !responsible) {
      toast.error("Please confirm all checklist items");
      return;
    }

    try {
      setLoading(true);

      const promises = selectedHandovers.map((handoverId) =>
        api.acknowledgeHandover(handoverId, {
          received: true,
          inspected: true,
          comments: comments,
        })
      );

      await Promise.all(promises);

      toast.success("Assets confirmed successfully!");
      setSelectedHandovers([]);
      setReceived(false);
      setInspected(false);
      setResponsible(false);
      setComments("");
      // Reset to page 1 and refresh data
      setCurrentPage(1);
      await fetchHandovers();
    } catch (error) {
      console.error("Error acknowledging handovers:", error);
      toast.error(
        "Error confirming assets: " + (error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReportIssue = async (issueText) => {
    if (selectedHandovers.length === 0) {
      toast.error("Please select at least one asset");
      return;
    }

    const finalIssues = (
      issueText !== undefined ? issueText : issues || ""
    ).trim();
    if (!finalIssues) {
      toast.error("Please describe the issue");
      return;
    }

    try {
      setLoading(true);

      const promises = selectedHandovers.map((handoverId) =>
        api.acknowledgeHandover(handoverId, {
          received: received,
          inspected: inspected,
          issues: finalIssues,
          comments: comments,
        })
      );

      await Promise.all(promises);

      toast.success("Issue reported successfully! Admin will be notified.");
      setSelectedHandovers([]);
      setReceived(false);
      setInspected(false);
      setResponsible(false);
      setComments("");
      setIssues("");
      setActionType(null);
      // Reset to page 1 and refresh data
      setCurrentPage(1);
      await fetchHandovers();
    } catch (error) {
      console.error("Error reporting issue:", error);
      toast.error(
        "Error reporting issue: " + (error.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusLabel = (status) => {
    if (status === "acknowledged" || status === "received") return "Received";
    return "pending";
  };

  const pendingCount = pendingFilter === "pending_only" ? totalHandovers : 0;

  if (!permissionsReady || !canViewMyAssetHandover) {
    return <Loader />;
  }

  if (loading && handovers.length === 0) {
    return <Loader />;
  }

  return (
    <div className="my-asset-handover-page" style={{ padding: "16px" }}>
      {/* Notification Banner */}
      {pendingCount > 0 && (
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
          }}
        >
          <div style={{ fontSize: "24px" }}>🔔</div>
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 600, marginBottom: "2px" }}
            >
              You have {pendingCount} asset{pendingCount > 1 ? "s" : ""} pending
              receipt
            </div>
            <div style={{ fontSize: "12px", opacity: 0.9 }}>
              Please review and confirm receipt of your assigned assets
            </div>
          </div>
        </div>
      )}

      {handovers.length === 0 ? (
        <div
          style={{
            background: "white",
            padding: "40px",
            borderRadius: "8px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ fontSize: "36px", marginBottom: "12px" }}>✅</div>
          <h3
            style={{
              margin: "0 0 6px 0",
              fontSize: "18px",
              fontWeight: 600,
              color: "#2d3748",
            }}
          >
            No Pending Handovers
          </h3>
          <p style={{ margin: 0, color: "#718096", fontSize: "13px" }}>
            You don't have any assets pending acknowledgement at this time.
          </p>
        </div>
      ) : (
        <>
          {/* Assets Table */}
          <div
            style={{
              background: "white",
              padding: "16px",
              borderRadius: "8px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                gap: "12px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
                Asset Handover Table
              </h2>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <label
                    htmlFor="pendingFilter"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#4a5568",
                    }}
                  >
                    Filter:
                  </label>
                  <select
                    id="pendingFilter"
                    value={pendingFilter}
                    onChange={(e) => {
                      setPendingFilter(e.target.value);
                      setCurrentPage(1);
                      setSelectedHandovers([]);
                    }}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#2d3748",
                      backgroundColor: "white",
                      cursor: "pointer",
                      minWidth: "150px",
                    }}
                  >
                    <option value="pending_only">Pending</option>
                    <option value="acknowledged_not_returned">Received</option>
                  </select>
                </div>
                <button
                  onClick={handleSelectAll}
                  style={{
                    padding: "6px 12px",
                    background: "#f7fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {selectedHandovers.length === handovers.length
                    ? "Deselect All"
                    : "Select All"}
                </button>
              </div>
            </div>

            <div className="scrollable-table-container">
              <table>
                <thead>
                  <tr className="table-header-row">
                    <th className="table-header-cell table-header-cell-checkbox">
                      <input
                        type="checkbox"
                        checked={
                          selectedHandovers.length === handovers.length &&
                          handovers.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="table-header-cell table-header-cell-serial">
                      S.No.
                    </th>
                    <th className="table-header-cell">Asset Tag ID</th>
                    <th className="table-header-cell table-header-cell-description">
                      Description
                    </th>
                    <th className="table-header-cell table-header-cell-brand">
                      Brand / Model
                    </th>
                    <th className="table-header-cell table-header-cell-location">
                      Location / Site
                    </th>
                    <th className="table-header-cell">Assigned Date</th>
                    <th className="table-header-cell">Assigned By</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell table-header-cell-actions">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {handovers.map((handover, index) => {
                    const handoverId = handover.id || handover._id;
                    const isSelected = selectedHandovers.includes(handoverId);
                    const asset = handover.asset || {};
                    const serialNumber =
                      (currentPage - 1) * itemsPerPage + index + 1;
                    return (
                      <tr
                        key={handoverId}
                        className={`table-body-row ${
                          isSelected ? "table-body-row-selected" : ""
                        }`}
                      >
                        <td className="table-body-cell-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectHandover(handoverId)}
                            className="table-checkbox"
                          />
                        </td>
                        <td className="table-body-cell-serial">
                          {serialNumber}
                        </td>
                        <td className="table-body-cell-asset-tag">
                          {asset.assetTag || "N/A"}
                        </td>
                        <td className="table-body-cell-description">
                          {asset.description || "N/A"}
                        </td>
                        <td className="table-body-cell-brand">
                          {asset.brand || "N/A"}{" "}
                          {asset.model ? ` / ${asset.model}` : ""}
                        </td>
                        <td className="table-body-cell-location">
                          {asset.location?.site?.name || "N/A"}{" "}
                          {asset.location?.locationDetail
                            ? ` / ${asset.location.locationDetail}`
                            : ""}
                        </td>
                        <td className="table-body-cell-default">
                          {formatDate(handover.assignedDate)}
                        </td>
                        <td className="table-body-cell-default">
                          {handover.assignedBy?.name || "N/A"}
                        </td>
                        <td className="table-body-cell">
                          <BadgeStatus
                            status={getStatusLabel(
                              handover.acknowledgementStatus || "pending"
                            )}
                          />
                        </td>
                        <td className="table-body-cell-actions">
                          <div className="table-actions-container">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(handoverId);
                              }}
                              style={{
                                padding: "6px 10px",
                                background: "#edf2f7",
                                color: "#4a5568",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "11px",
                                fontWeight: 500,
                              }}
                              title="View Details"
                            >
                              👁️ View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {handovers.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalHandovers}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(newItemsPerPage) => {
                  setItemsPerPage(newItemsPerPage);
                  setCurrentPage(1);
                }}
                showItemsPerPage={true}
              />
            )}
          </div>

          {/* Checklist / Confirmation Area */}
          {selectedHandovers.length > 0 && (
            <div
              style={{
                background: "white",
                padding: "16px",
                borderRadius: "8px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                marginBottom: "16px",
              }}
            >
              <h2
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                Confirm Receipt ({selectedHandovers.length} asset
                {selectedHandovers.length > 1 ? "s" : ""} selected)
              </h2>

              <div style={{ marginBottom: "16px" }}>
                <HandoverChecklist
                  received={received}
                  inspected={inspected}
                  responsible={responsible}
                  onReceivedChange={setReceived}
                  onInspectedChange={setInspected}
                  onResponsibleChange={setResponsible}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <HandoverCommentsBox
                  value={comments}
                  onCommentChange={setComments}
                  placeholder="Example: Bag is missing one SFP module."
                />
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => {
                    setActionType("confirm");
                    handleConfirmReceived();
                  }}
                  disabled={loading || !received || !inspected || !responsible}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background:
                      !received || !inspected || !responsible
                        ? "#cbd5e0"
                        : "linear-gradient(135deg, #38a169 0%, #2f855a 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor:
                      !received || !inspected || !responsible
                        ? "not-allowed"
                        : "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading && actionType === "confirm"
                    ? "Processing..."
                    : "✓ Confirm Received"}
                </button>

                <button
                  onClick={() => {
                    if (!issues.trim()) {
                      setShowIssueModal(true);
                    } else {
                      setActionType("report");
                      handleReportIssue(issues);
                    }
                  }}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background:
                      "linear-gradient(135deg, #e53e3e 0%, #c53030 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {loading && actionType === "report"
                    ? "Processing..."
                    : "⚠ Report Issue"}
                </button>
              </div>

              {actionType === "report" && (
                <div style={{ marginTop: "12px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#4a5568",
                    }}
                  >
                    Issue Description *
                  </label>
                  <textarea
                    value={issues}
                    onChange={(e) => setIssues(e.target.value)}
                    placeholder="Describe the issue with the asset..."
                    rows="4"
                    required
                    style={{
                      width: "100%",
                      padding: "12px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Issue Description Modal */}
      <IssueDescriptionModal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        onSubmit={(issueText) => {
          setIssues(issueText);
          setActionType("report");
          handleReportIssue(issueText);
        }}
      />
    </div>
  );
};

export default MyAssetHandoverPage;
